import { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getEmployeesByBranch, getEmployeePhotoUrl, getMonthlyHighlightEmployee } from '../../../../../services/PublicBookingService';
import ImageWithSkeleton from '../../../../components/Custom/Common/ImageWithSkeleton';

const RANDOM_EMPLOYEE = {
    id: 'random',
    isRandom: true,
    name: 'Escolher automaticamente'
};

const StepEmployee = ({ companyUuid, branchId, onSelect, onBack, selectedEmployee, customer = {}, selectedServices = [] }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [error, setError] = useState('');
    const [failedPhotos, setFailedPhotos] = useState(new Set()); // Rastreia fotos que falharam
    const [highlightEmployeeId, setHighlightEmployeeId] = useState(null); // Colaborador destaque do mês (selo)

    // Recarrega também quando o cliente identificado muda: a lista esconde quem não atende
    // assinante, e trocar de cliente pode mudar quem aparece.
    useEffect(() => {
        loadEmployees();
        loadHighlightEmployee();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branchId, customer.document, customer.phone, selectedServices]);

    const loadEmployees = async () => {
        try {
            setLoading(true);
            const response = await getEmployeesByBranch(companyUuid, branchId, customer, selectedServices);
            setEmployees(response.data);
        } catch (err) {
            console.error("Error loading employees:", err);
            setError(t('booking.load_employees_error'));
        } finally {
            setLoading(false);
        }
    };

    // Busca best-effort: se falhar ou ninguém tiver vendido nada no mês (204), simplesmente
    // não exibe o selo de destaque, sem bloquear a escolha de profissional.
    const loadHighlightEmployee = async () => {
        try {
            const response = await getMonthlyHighlightEmployee(companyUuid, branchId);
            setHighlightEmployeeId(response.data?.id ?? null);
        } catch (err) {
            setHighlightEmployeeId(null);
        }
    };

    const handleSelect = (employee) => {
        onSelect(employee.isRandom ? { ...employee, candidateEmployees: employees } : employee);
    };

    // Obtém o nome do colaborador (suporta diferentes formatos da API)
    const getEmployeeName = (employee) => {
        if (employee.name) return employee.name;
        if (employee.fullName) return employee.fullName;
        if (employee.firstName && employee.lastName) {
            return `${employee.firstName} ${employee.lastName}`;
        }
        if (employee.firstName) return employee.firstName;
        return 'Profissional';
    };

    // Gera iniciais do nome
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Verifica se deve mostrar foto ou iniciais
    const shouldShowPhoto = (employee) => {
        return !failedPhotos.has(employee.id);
    };

    // Callback quando a foto falha ao carregar
    const handlePhotoError = (employeeId) => {
        setFailedPhotos(prev => new Set([...prev, employeeId]));
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">{t('booking.loading_professionals')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-5">
                <i className="fa fa-exclamation-circle fa-3x text-danger mb-3"></i>
                <p className="text-danger">{error}</p>
                <Button variant="outline-primary" onClick={onBack}>
                    <i className="fa fa-arrow-left me-2"></i>
                    {t('back')}
                </Button>
            </div>
        );
    }

    if (!employees || employees.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="fa fa-user-tie fa-3x text-muted mb-3"></i>
                <p className="text-muted">{t('booking.no_professionals')}</p>
                <Button variant="outline-primary" onClick={onBack}>
                    <i className="fa fa-arrow-left me-2"></i>
                    {t('back')}
                </Button>
            </div>
        );
    }

    return (
        <div className="step-employee">
            <div className="step-header mb-4">
                <h4 className="step-title">
                    <i className="fa fa-user-tie me-2 text-primary"></i>
                    {t('booking.select_professional')}
                </h4>
                <p className="step-subtitle text-muted">
                    {t('booking.select_professional_description')}
                </p>
            </div>

            <Row className="g-3 mb-4">
                <Col xs={6} sm={4} md={4}>
                    <Card
                        className={`employee-card employee-card-random h-100 ${selectedEmployee?.isRandom ? 'selected' : ''}`}
                        onClick={() => handleSelect(RANDOM_EMPLOYEE)}
                        role="radio"
                        aria-checked={Boolean(selectedEmployee?.isRandom)}
                        tabIndex={0}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleSelect(RANDOM_EMPLOYEE);
                            }
                        }}
                    >
                        <div className="employee-photo-wrap employee-photo-wrap-random">
                            <i className="fa fa-shuffle employee-random-icon"></i>
                            {selectedEmployee?.isRandom && (
                                <div className="employee-selected-badge">
                                    <i className="fa fa-check"></i>
                                </div>
                            )}
                        </div>
                        <Card.Body className="text-center p-2 p-sm-3">
                            <h6 className="employee-name mb-0">Qualquer profissional</h6>
                            <small className="text-muted d-block mt-1">O sistema escolhe para você</small>
                        </Card.Body>
                    </Card>
                </Col>
                {employees.map((employee) => (
                    <Col xs={6} sm={4} md={4} key={employee.id}>
                        <Card
                            className={`employee-card h-100 ${selectedEmployee?.id === employee.id ? 'selected' : ''}`}
                            onClick={() => handleSelect(employee)}
                            role="radio"
                            aria-checked={selectedEmployee?.id === employee.id}
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    handleSelect(employee);
                                }
                            }}
                        >
                            <div className="employee-photo-wrap">
                                {shouldShowPhoto(employee) ? (
                                    // onDark: a moldura da foto usa a cor da marca, quase sempre escura.
                                    <ImageWithSkeleton
                                        src={getEmployeePhotoUrl(companyUuid, employee.id)}
                                        alt={getEmployeeName(employee)}
                                        className="employee-photo-img"
                                        fill
                                        onDark
                                        onError={() => handlePhotoError(employee.id)}
                                    />
                                ) : (
                                    <span className="employee-initials-lg">
                                        {getInitials(getEmployeeName(employee))}
                                    </span>
                                )}
                                {selectedEmployee?.id === employee.id && (
                                    <div className="employee-selected-badge">
                                        <i className="fa fa-check"></i>
                                    </div>
                                )}
                                {highlightEmployeeId === employee.id && (
                                    <div className="employee-highlight-badge" title={t('performance.highlight_of_month')}>
                                        <i className="fa fa-trophy"></i>
                                    </div>
                                )}
                            </div>
                            <Card.Body className="text-center p-2 p-sm-3">
                                <h6 className="employee-name mb-0">{getEmployeeName(employee)}</h6>
                                {highlightEmployeeId === employee.id && (
                                    <small className="employee-highlight-label d-block mt-1">
                                        <i className="fa fa-trophy me-1"></i>{t('performance.highlight_of_month')}
                                    </small>
                                )}
                                {employee.specialty && (
                                    <small className="text-muted d-block mt-1">{employee.specialty}</small>
                                )}
                                {employee.rating && (
                                    <div className="mt-1">
                                        <i className="fa fa-star text-warning me-1" style={{ fontSize: '0.75rem' }}></i>
                                        <small>{employee.rating.toFixed(1)}</small>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Botões de navegação */}
            <div className="step-actions d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={onBack}>
                    <i className="fa fa-arrow-left me-2"></i>
                    {t('back')}
                </Button>
            </div>
        </div>
    );
};

export default StepEmployee;
